/* BOGO picker + sticky add-to-cart. Design: mockup/pdp-mobile.html (approved 4 Oct 2026).
   No selection = the product form works exactly as before. */
(() => {
  if (customElements.get('bogo-picker')) return;

  const TWO_LABEL = 'Add 2 products to cart';
  const defaultLabel = () => (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Only used with "Witte productachtergrond" OFF: read the original photo's own background colour
  // (median of its border) and give the image area that colour, so no edge shows. The photo itself is
  // never altered: no CSS filter, no blend mode, nothing drawn back. With the setting ON the images are
  // pre-processed files with an exact #FFFFFF background and nothing runs here.
  const bgCache = new Map();
  function measure(img) {
    if (img.closest('.is-white-bg')) return;
    const key = img.currentSrc || img.src;
    let bg = bgCache.get(key);
    if (bg === undefined) {
      try {
        const w = Math.min(img.naturalWidth, 200);
        const h = Math.round(w * img.naturalHeight / img.naturalWidth);
        if (!w || !h) return;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const b = 3, mins = [], rs = [], gs = [], bs = [];
        const take = (x, y, cw, ch) => {
          const d = ctx.getImageData(x, y, cw, ch).data;
          for (let i = 0; i < d.length; i += 4) {
            mins.push(Math.min(d[i], d[i + 1], d[i + 2])); rs.push(d[i]); gs.push(d[i + 1]); bs.push(d[i + 2]);
          }
        };
        take(0, 0, w, b); take(0, h - b, w, b); take(0, b, b, h - 2 * b); take(w - b, b, b, h - 2 * b);
        const sorted = (arr) => arr.slice().sort((x, y) => x - y);
        const med = (arr) => sorted(arr)[arr.length >> 1];
        bg = sorted(mins)[Math.floor(mins.length * 0.02)] >= 220 ? `rgb(${med(rs)}, ${med(gs)}, ${med(bs)})` : '';
        bgCache.set(key, bg);
      } catch (e) { return; /* canvas blocked: CSS fallback colour stays */ }
    }
    if (!bg) return;
    img.style.setProperty('--bogo-photo-bg', bg);
    if (img.parentElement.classList.contains('bogo-card__img')) img.parentElement.style.setProperty('--bogo-photo-bg', bg);
  }

  function whenLoaded(img) {
    if (img.closest('.is-white-bg')) return;
    if (img.complete && img.naturalWidth) measure(img);
    else img.addEventListener('load', () => measure(img), { once: true });
  }

  function setupImages(scope) {
    scope.querySelectorAll('.bogo-card__img img').forEach(whenLoaded);
  }

  class BogoPicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.form = document.getElementById(this.dataset.formId);
      this.productForm = this.form && this.form.closest('product-form');
      this.row = this.querySelector('[data-bogo-row]');
      this.selection = null; // { handle, variantId, size, data }
      this.dataCache = new Map();

      // Overlays live in <body> so position: fixed works regardless of ancestors
      this.overlays = this.querySelector('[data-bogo-overlays]');
      document.body.appendChild(this.overlays);
      this.q = (sel) => this.overlays.querySelector(sel);
      this.sheet = this.q('[data-bogo-sheet]');
      this.browseSheet = this.q('[data-bogo-browse-sheet]');
      this.scrim = this.q('[data-bogo-scrim]');
      this.grid = this.q('[data-bogo-grid]');

      setupImages(this);
      this.row.addEventListener('click', (e) => this.onCardClick(e));
      this.grid.addEventListener('click', (e) => this.onCardClick(e));

      // Desktop: one page = 3 cards (row width + gap). Mobile (arrows only shown with a mouse): 2 cards.
      const desktop = window.matchMedia('(min-width: 750px)');
      const step = () => desktop.matches
        ? this.row.clientWidth + 8
        : ((this.row.querySelector('.bogo-card:not(.is-hidden)')?.offsetWidth || 150) + 8) * 2;
      this.prevBtn = this.querySelector('[data-bogo-prev]');
      this.nextBtn = this.querySelector('[data-bogo-next]');
      this.prevBtn.addEventListener('click', () => this.row.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.nextBtn.addEventListener('click', () => this.row.scrollBy({ left: step(), behavior: 'smooth' }));
      const syncArrows = () => {
        const max = this.row.scrollWidth - this.row.clientWidth;
        this.prevBtn.disabled = this.row.scrollLeft <= 1;
        this.nextBtn.disabled = this.row.scrollLeft >= max - 1;
      };
      this.row.addEventListener('scroll', syncArrows, { passive: true });
      window.addEventListener('resize', syncArrows);
      this.syncArrows = syncArrows;
      syncArrows();
      this.querySelector('[data-bogo-browse]').addEventListener('click', () => this.openBrowse());

      this.overlays.querySelectorAll('[data-bogo-close]').forEach((b) => b.addEventListener('click', () => this.closeTop()));
      this.scrim.addEventListener('click', () => this.closeTop());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeTop(); });
      this.q('[data-bogo-sizes]').addEventListener('click', (e) => this.onSizeClick(e));
      this.q('[data-bogo-sheet-cta]').addEventListener('click', () => this.onSheetCta());
      const gallery = this.q('[data-bogo-gallery]');
      gallery.addEventListener('scroll', () => {
        const i = Math.round(gallery.scrollLeft / Math.max(gallery.clientWidth, 1));
        [...this.q('[data-bogo-dots]').children].forEach((d, j) => d.classList.toggle('is-on', i === j));
      }, { passive: true });
      this.q('[data-bogo-q]').addEventListener('input', () => this.filterGrid());

      this.hookForm();
      this.checkLiveAvailability();
    }

    // ---------- data ----------
    data(card) {
      const h = card.dataset.handle;
      if (!this.dataCache.has(h)) {
        try { this.dataCache.set(h, JSON.parse(card.querySelector('[data-bogo-card-data]').textContent)); }
        catch (e) { this.dataCache.set(h, null); }
      }
      return this.dataCache.get(h);
    }

    // Stock in the page can be a few minutes old: re-check each partner once, at variant level.
    checkLiveAvailability() {
      const run = () => {
        this.row.querySelectorAll('.bogo-card').forEach((card) => {
          fetch(`${root()}products/${card.dataset.handle}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .then((p) => {
              if (!p) return;
              const d = this.data(card);
              const avail = new Map(p.variants.map((v) => [v.id, v.available]));
              if (d) {
                d.variants.forEach((v) => { if (avail.has(v.id)) v.available = avail.get(v.id); });
                if (!avail.get(d.variantId)) {
                  const first = d.variants.find((v) => v.available);
                  if (first) d.variantId = first.id;
                }
              }
              if (!p.variants.some((v) => v.available)) {
                card.classList.add('is-hidden');
                this.syncArrows && this.syncArrows();
                if (this.selection && this.selection.handle === card.dataset.handle) this.deselect();
              }
            })
            .catch(() => {});
        });
      };
      if (!('IntersectionObserver' in window)) return run();
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); run(); }
      }, { rootMargin: '400px 0px' });
      io.observe(this);
    }

    // ---------- cards ----------
    onCardClick(e) {
      const btn = e.target.closest('[data-bogo-act]');
      if (!btn) return;
      const card = btn.closest('.bogo-card');
      const d = this.data(card);
      if (!d) return;
      if (btn.dataset.bogoAct === 'view') return this.openSheet(card);
      if (this.selection && this.selection.handle === d.handle) return this.deselect();
      if (d.hasSizes) return this.openSheet(card);
      this.select(card, d.variantId, null);
      this.close(this.browseSheet);
    }

    cardsFor(handle) {
      return [...this.row.querySelectorAll(`.bogo-card[data-handle="${CSS.escape(handle)}"]`),
              ...this.grid.querySelectorAll(`.bogo-card[data-handle="${CSS.escape(handle)}"]`)];
    }

    select(card, variantId, size) {
      const d = this.data(card);
      // A piece chosen from "Browse all" that is not in the row moves to the front of the row
      if (!this.row.querySelector(`.bogo-card[data-handle="${CSS.escape(d.handle)}"]`)) {
        const clone = card.cloneNode(true);
        clone.classList.remove('is-on', 'is-hidden');
        this.row.prepend(clone);
        const visible = [...this.row.querySelectorAll('.bogo-card:not(.is-hidden)')];
        if (visible.length > 10) visible[visible.length - 1].remove();
        setupImages(clone);
        this.row.scrollTo({ left: 0, behavior: 'smooth' });
      }
      this.selection = { handle: d.handle, variantId, size, data: d };
      this.render();
      this.syncArrows && this.syncArrows();
    }

    deselect() {
      this.selection = null;
      this.render();
    }

    render() {
      const sel = this.selection;
      this.overlays.querySelectorAll('.bogo-card').forEach((c) => this.paintCard(c, sel));
      this.querySelectorAll('.bogo-card').forEach((c) => this.paintCard(c, sel));
      this.updateMainButton();
      window.dispatchEvent(new CustomEvent('bogo:selection-change', { detail: { selected: !!sel } }));
    }

    paintCard(card, sel) {
      const d = this.data(card);
      const on = !!(sel && d && sel.handle === d.handle);
      card.classList.toggle('is-on', on);
      const btn = card.querySelector('[data-bogo-act="choose"]');
      btn.textContent = on ? 'ADDED ✓' : 'CHOOSE';
      btn.setAttribute('aria-pressed', String(on));
      if (d) card.querySelector('[data-bogo-var]').textContent = d.metal + (on && sel.size ? ' · ' + sel.size : '');
    }

    // ---------- bottom sheet ----------
    openSheet(card) {
      const d = this.data(card);
      this.sheetCard = card;
      this.sheetSize = this.selection && this.selection.handle === d.handle ? this.selection.size : null;
      this.q('[data-bogo-sheet-name]').textContent = d.name;
      this.q('[data-bogo-sheet-var]').textContent = d.metal;
      const gallery = this.q('[data-bogo-gallery]');
      gallery.innerHTML = '';
      d.images.forEach((im, i) => {
        const img = document.createElement('img');
        // Pre-processed white packshot when available (setting ON), otherwise the original photo
        img.src = im.white || im.src;
        if (im.white) {
          img.className = 'is-white';
          img.addEventListener('error', () => { img.classList.remove('is-white'); img.src = im.src; }, { once: true });
        } else {
          img.crossOrigin = 'anonymous';
        }
        img.alt = `${d.name} ${i + 1} / ${d.images.length}`;
        if (i) img.loading = 'lazy';
        gallery.appendChild(img);
        if (!im.white) whenLoaded(img);
      });
      gallery.scrollLeft = 0;
      this.q('[data-bogo-dots]').innerHTML = d.images.map((_, i) => `<i class="${i === 0 ? 'is-on' : ''}"></i>`).join('');
      this.q('[data-bogo-sheet-price]').innerHTML = card.querySelector('.bogo-card__price').innerHTML.replace(/<br\s*\/?>/i, ' ');
      const wrap = this.q('[data-bogo-sizes-wrap]');
      wrap.hidden = !d.hasSizes;
      if (d.hasSizes) {
        const sizes = this.q('[data-bogo-sizes]');
        sizes.innerHTML = '';
        d.variants.forEach((v) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'bogo-size' + (v.title === this.sheetSize ? ' is-on' : '');
          b.textContent = v.title;
          b.dataset.variantId = v.id;
          if (!v.available) { b.disabled = true; b.setAttribute('aria-label', `${v.title} sold out`); }
          sizes.appendChild(b);
        });
      }
      this.updateSheetCta();
      this.open(this.sheet);
    }

    sheetIsPicked() {
      const d = this.data(this.sheetCard);
      return !!(this.selection && this.selection.handle === d.handle && (!d.hasSizes || this.selection.size === this.sheetSize));
    }

    updateSheetCta() {
      const d = this.data(this.sheetCard);
      const needSize = d.hasSizes && !this.sheetSize;
      this.q('[data-bogo-size-label]').textContent = this.sheetSize || 'Select a size';
      const cta = this.q('[data-bogo-sheet-cta]');
      cta.disabled = needSize;
      cta.textContent = this.sheetIsPicked() ? 'ADDED ✓' : needSize ? 'SELECT A SIZE' : 'CHOOSE';
    }

    onSizeClick(e) {
      const b = e.target.closest('.bogo-size');
      if (!b || b.disabled) return;
      this.sheetSize = b.textContent;
      this.sheetVariantId = Number(b.dataset.variantId);
      [...this.q('[data-bogo-sizes]').children].forEach((x) => x.classList.toggle('is-on', x === b));
      this.updateSheetCta();
    }

    onSheetCta() {
      const d = this.data(this.sheetCard);
      const wasPicked = this.sheetIsPicked();
      if (wasPicked) {
        this.deselect();
      } else {
        const variantId = d.hasSizes
          ? (this.sheetVariantId || (d.variants.find((v) => v.title === this.sheetSize) || {}).id)
          : d.variantId;
        this.select(this.sheetCard, variantId, d.hasSizes ? this.sheetSize : null);
      }
      this.close(this.sheet);
      if (!wasPicked) this.close(this.browseSheet);
    }

    // ---------- browse all ----------
    openBrowse() {
      this.q('[data-bogo-q]').value = '';
      this.open(this.browseSheet);
      if (this.browseLoaded) return this.filterGrid();
      this.browseLoaded = true;
      this.grid.innerHTML = '<div class="bogo-grid__msg">Loading…</div>';
      fetch(this.dataset.browseUrl)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const list = doc.querySelector('.bogo-browse-list');
          this.grid.innerHTML = list ? list.innerHTML : '';
          setupImages(this.grid);
          this.render();
          this.filterGrid();
        })
        .catch(() => {
          this.browseLoaded = false;
          this.grid.innerHTML = '<div class="bogo-grid__msg">Could not load pieces</div>';
        });
    }

    filterGrid() {
      const q = this.q('[data-bogo-q]').value.trim().toLowerCase();
      let shown = 0;
      this.grid.querySelectorAll('.bogo-card').forEach((c) => {
        const d = this.data(c);
        const match = !q || (d && `${d.name} ${d.metal}`.toLowerCase().includes(q));
        c.hidden = !match;
        if (match) shown++;
      });
      let msg = this.grid.querySelector('.bogo-grid__msg--empty');
      if (!shown && this.grid.querySelector('.bogo-card')) {
        if (!msg) {
          msg = document.createElement('div');
          msg.className = 'bogo-grid__msg bogo-grid__msg--empty';
          msg.textContent = 'No pieces found';
          this.grid.appendChild(msg);
        }
      } else if (msg) msg.remove();
    }

    // ---------- sheet helpers ----------
    open(el) {
      el.classList.add('is-open');
      this.scrim.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
    }
    close(el) {
      el.classList.remove('is-open');
      if (!this.sheet.classList.contains('is-open') && !this.browseSheet.classList.contains('is-open')) {
        this.scrim.classList.remove('is-open');
        document.documentElement.style.overflow = '';
      }
    }
    closeTop() {
      if (this.sheet.classList.contains('is-open')) this.close(this.sheet);
      else if (this.browseSheet.classList.contains('is-open')) this.close(this.browseSheet);
    }

    // ---------- main button + add to cart ----------
    updateMainButton() {
      if (!this.productForm) return;
      const btn = this.productForm.querySelector('[type="submit"]');
      const span = btn && btn.querySelector('span');
      if (!span || btn.hasAttribute('disabled')) return;
      const want = this.selection ? TWO_LABEL : defaultLabel();
      if (span.textContent.trim() !== want) span.textContent = want;
    }

    hookForm() {
      if (!this.productForm) return;
      const btn = this.productForm.querySelector('[type="submit"]');
      const span = btn && btn.querySelector('span');
      // The theme rewrites the button text on variant changes: put our label back if needed
      if (span) {
        new MutationObserver(() => {
          if (this.selection && span.textContent.trim() === defaultLabel()) this.updateMainButton();
        }).observe(span, { childList: true, characterData: true, subtree: true });
      }
      // Capture phase on the <product-form> runs before the theme's own submit handler on the <form>
      this.productForm.addEventListener('submit', (e) => {
        if (!this.selection) return; // no choice: theme handles it exactly as before
        e.preventDefault();
        e.stopPropagation();
        this.addBoth();
      }, true);
    }

    addBoth() {
      const pf = this.productForm;
      const btn = pf.querySelector('[type="submit"]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      if (typeof pf.handleErrorMessage === 'function') pf.handleErrorMessage();
      btn.setAttribute('aria-disabled', true);
      btn.classList.add('loading');
      pf.querySelector('.loading__spinner')?.classList.remove('hidden');

      const main = new FormData(this.form);
      const body = new FormData();
      body.append('items[0][id]', main.get('id'));
      body.append('items[0][quantity]', main.get('quantity') || 1);
      for (const [k, v] of main.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m) body.append(`items[0][properties][${m[1]}]`, v);
      }
      body.append('items[1][id]', this.selection.variantId);
      body.append('items[1][quantity]', 1);
      body.append('items[1][properties][_bogo_pick]', 'picker');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append('sections', cart.getSectionsToRender().map((s) => s.section));
        body.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = body;

      fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}`, config)
        .then((r) => r.json())
        .then((res) => {
          if (res.status) {
            if (typeof pf.handleErrorMessage === 'function') pf.handleErrorMessage(res.description || res.message);
            return;
          }
          if (!cart) { window.location = (window.routes && window.routes.cart_url) || '/cart'; return; }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: main.get('id'), cartData: res });
          }
          cart.renderContents(res);
          this.deselect();
        })
        .catch((e) => console.error(e))
        .finally(() => {
          btn.classList.remove('loading');
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          btn.removeAttribute('aria-disabled');
          pf.querySelector('.loading__spinner')?.classList.add('hidden');
        });
    }
  }
  customElements.define('bogo-picker', BogoPicker);

  // ---------- sticky add-to-cart ----------
  class BogoStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const form = document.getElementById(this.dataset.formId);
      this.mainBtn = form && form.querySelector('[type="submit"]');
      if (!this.mainBtn) return;
      this.settingOn = this.dataset.settingEnabled === 'true';
      this.selected = false;
      this.inView = true;
      this.scrolledPast = false;
      this.btn = this.querySelector('[data-bogo-sticky-btn]');
      this.label = this.btn.querySelector('span');

      document.body.appendChild(this);
      this.hidden = false;

      this.btn.addEventListener('click', () => this.mainBtn.click());
      window.addEventListener('bogo:selection-change', (e) => { this.selected = e.detail.selected; this.sync(); });

      const header = document.querySelector('sticky-header, .header-wrapper, .section-header');
      const top = header ? Math.round(header.getBoundingClientRect().height) : 0;
      new IntersectionObserver(([en]) => {
        this.inView = en.isIntersecting;
        this.scrolledPast = !en.isIntersecting && en.boundingClientRect.top < (en.rootBounds ? en.rootBounds.top : 0);
        this.sync();
      }, { rootMargin: `-${top}px 0px 0px 0px`, threshold: 0 }).observe(this.mainBtn);
    }

    sync() {
      const forPick = this.selected && !this.inView;
      const forSetting = this.settingOn && this.scrolledPast;
      const disabled = this.mainBtn.hasAttribute('disabled');
      this.label.textContent = this.selected ? TWO_LABEL : (this.mainBtn.querySelector('span')?.textContent.trim() || defaultLabel());
      this.classList.toggle('is-visible', (forPick || forSetting) && !disabled);
    }
  }
  customElements.define('bogo-sticky-atc', BogoStickyAtc);
})();
